export type PictureQuality  = 'Low' | 'Normal' | 'High';
export type GridDensity     = 'Low' | 'Normal' | 'High' | 'VeryHigh';
export type PreviewTexturePx = 512 | 1024 | 1536;

export interface MachineTypeEntry {
  id:       number;
  widthMm:  number;
  heightMm: number;
}

export const MACHINE_TYPES: MachineTypeEntry[] = [
  { id: 1, widthMm: 350, heightMm: 350 },
  { id: 2, widthMm: 400, heightMm: 400 },
  { id: 3, widthMm: 500, heightMm: 500 },
];

export const PICTURE_QUALITY_PX: Record<PictureQuality, number> = {
  Low:    1000,
  Normal: 2000,
  High:   3200,
};

export const MATERIAL_COLORS: { name: string; hex: string }[] = [
  { name: 'Blue',   hex: '#1e22aa' },
  { name: 'Red',    hex: '#cc2222' },
  { name: 'Green',  hex: '#228822' },
  { name: 'Yellow', hex: '#cccc00' },
  { name: 'Orange', hex: '#cc6600' },
  { name: 'White',  hex: '#dddddd' },
  { name: 'Gray',   hex: '#888888' },
  { name: 'Black',  hex: '#222222' },
];

export interface AppState {
  materialWidth:      number;   // mm  (XSize)
  materialHeight:     number;   // mm  (YSize)
  materialColor:      string;   // hex
  maxBlowHeight:      number;   // mm
  pressureFraction:   number;   // 0..1 – ułamek obliczonego ciśnienia (1.0 = bez zmiany)
  currentHeight:      number;   // mm
  showGridCm:         boolean;
  showGridMm:         boolean;
  showMargin:         boolean;
  negGMColor:         boolean;
  pullUpMaxH:         boolean;  // true = po zbieżności rozciągnij Z do maxBlowHeight
  showComputed:       boolean;
  showVisible:        boolean;
  showFullMaterial:   boolean;  // true = pokaż pełny arkusz (z czerwonymi obszarami)
  currentOpening:     number;
  totalOpenings:      number;
  // Parameters dialog
  marginMm:           number;
  thicknessMm:        number;
  opMarginMm:         number;
  transparency:       number;   // 0–100
  materialAboveImage: boolean;
  multipleContours:   boolean;
  pictureQuality:     PictureQuality;
  gridDensity:        GridDensity;
  machineType:        number;
  previewTexturePx:   PreviewTexturePx;
  distortionVersion:  number;   // algorytm pre-deformacji (1 = radialny)
  distortionStrength: number;   // siła deformacji 0..2 (1.0 = pełna)
  // FEM solver tuning
  femRampLevels:       number;  // liczba stopni quasi-statycznego rozruchu ciśnienia (50–500)
  femRampCheckPeriod:  number;  // kroków FEM na jeden stopień rampy (100–2000)
  femBendingFactor:    number;  // mnożnik sztywności na zginanie (0=wyłączone, 1=fizyczna)
  femBoostFactor:      number;  // mnożnik ciśnienia przy każdym booście (1.01–1.20)
  femMaterialFailRatio: number; // stosunek pola trójkąta → stop przy zniszczeniu materiału (2–20)
}

export type AppStateUpdate = Partial<AppState>;

/** Typ warstwy obrazu */
export type LayerType = 'S' | 'F' | 'A';  // Shaped | Flat | All

export interface Layer {
  id:               string;
  fileName:         string;
  imageDataUrl:     string;   // pełny obraz (data URL)
  thumbnailDataUrl: string;   // miniatura 60×60 px
  type:             LayerType;
  translateX:       number;   // przesunięcie w pikselach canvasu
  translateY:       number;
  rotate:           number;   // stopnie
  baseScale:        number;   // skala wypełnienia canvasu (stała, wyznaczana przy ładowaniu)
  scale:            number;   // mnożnik użytkownika (1.0 = rozmiar wypełnienia)
}

export interface TemplateInfo {
  dataUrl:   string;
  widthMm:   number;
  heightMm:  number;
}

export interface ContourPoint {
  x: number;   // mm, origin = centre of material
  y: number;
}

export interface Contour {
  points:  ContourPoint[];
  area:    number;   // mm²
  isHole:  boolean;
}

export interface VectorizeResult {
  imageSize:            { widthPx: number; heightPx: number };
  contours:             Contour[];  // obszary otworów szablonu (białe)
  cutContours:          Contour[];  // przypadek A: wewnętrzne regiony czerwone
  cutContoursInverted:  Contour[];  // przypadek B: wyspy nie-czerwone otoczone czerwienią
  outerBoundary:        ContourPoint[];
}

export interface TriangulationResult {
  vertices:     { x: number; y: number }[];
  triangles:    [number, number, number][];
  triLabels:    number[];   // 0=material, N=inside opening N (1-based)
  triCutLabels: number[];   // 0=zachowaj, 1=wycięty (czerwony obszar szablonu)
  nodeLabels:   number[];   // 0=interior, 1=outer boundary, 2+h=hole h boundary
  nodeRegions:  number[];   // 0=material, N=inside opening N (1-based)
  stats: {
    vertexCount:   number;
    triangleCount: number;
    openingCount:  number;
  };
}

export type SimState = 'idle' | 'running' | 'paused' | 'done' | 'error';

export interface UserInfo {
  login:        string;
  fullName:     string;
  isAdmin:      boolean;
  canPicLayers: boolean;
}

export interface AuthState {
  user:  UserInfo | null;
  token: string   | null;
}
